import React from 'react'
import { Box, Button } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { AppNote, AppStartHint, secondaryButtonSx } from '@components'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { fmtInstant, hiddenSimulated, noPushesTitle } from '../@services/windowLabel'


/** No pass decided anything in the window: the hint, the last pass's time, and the way to it. */
export function NoPassHint({ data, onShowLastPass }) {
	const tz = data?.window?.tz
	const last = data?.last_pass?.ts ? fmtInstant(data.last_pass.ts, tz) : null
	const sample = data?.sample
	return <AppStartHint icon="clock-rotate-left" title="The engine has not pushed anything in this window"
		description={<>
			{last ? `Last pass for ${sample?.office} · ${sample?.lor} days: ${last}${tz ? ` (${tz})` : ''}, outside this window.` : `No pass has decided ${sample?.office} · ${sample?.lor} days for these pick-up dates in this window.`}
			{' '}Widen the window or open <RouterLink to={DS_REPRICING_ROUTES.EXECUTIONS_PATH}>Executions</RouterLink> to see when the next pass runs.
			<Box sx={{ mt: 2 }}>
				{data?.window?.kind !== 'last_pass' && <Button variant="outlined" onClick={onShowLastPass} sx={secondaryButtonSx}>Show the last pass</Button>}
			</Box>
		</>}/>
}

/**
 * Criterion 10b: passes decided the sample but nothing counts — said as such, in place of the cards, so no 0 % reads
 * as a result (brief: "Sin rate pushes entre … y …"). The map and the pushes stay: they say why. v1.5: when the
 * Exclude filter is what left nothing, it says how many simulated decisions it hides.
 */
export function NoPushesNote({ data }) {
	const n = data?.passes || 0
	const hidden = hiddenSimulated(data)
	if (hidden) {
		return <AppNote severity="info" sx={{ mb: 3 }}>
			<b>Exclude left nothing: {hidden} simulated {hidden === 1 ? 'decision' : 'decisions'} hidden.</b> No real write moved your prices in this window;
			{' '}set Simulated to Include to see what the engine would have moved, marked SIM.
		</AppNote>
	}
	return <AppNote severity="info" sx={{ mb: 3 }}>
		<b>{noPushesTitle(data)}.</b> {n} {n === 1 ? 'pass' : 'passes'} decided these cells, but none of their decisions reached the PMS (already set, no price change or not sent), so your prices did not move.
		{' '}The map still shows your tier; open a cell to see each decision's resolution, or open <RouterLink to={DS_REPRICING_ROUTES.ACTIVITY_PATH}>Activity log</RouterLink>.
	</AppNote>
}

export function InProgressNote() {
	return <AppNote severity="info" sx={{ mb: 2 }}>Pass in progress: the newest pass has not finished yet, so what you see is what has landed so far. The figures will fill in as its sends land.</AppNote>
}

export function NoTierNote() {
	return <AppNote severity="warning" sx={{ mb: 2 }}>
		No tier to compare with: the strategies that priced these cells compare against no tier, so there is no market half. Your prices and the pushes are complete;
		pick a tier in <RouterLink to={DS_REPRICING_ROUTES.STRATEGIES_PATH}>Strategies</RouterLink> to see the market.
	</AppNote>
}

export function ErrorNote({ error, onRetry }) {
	return <AppNote severity="error" sx={{ mb: 2, alignItems: 'center' }}>
		<Box sx={{ flex: 1 }}>{error} The filters are kept.</Box>
		<Button variant="outlined" size="small" onClick={onRetry} sx={{ ...secondaryButtonSx, height: 32, fontSize: 13 }}>Try again</Button>
	</AppNote>
}

/**
 * The mode banner of this screen: the account's mode, and what the simulated filter does to the figures (v1.5:
 * simulated decisions count exactly like real ones, marked SIM, unless Simulated is on Exclude). It changes no figure.
 */
export function ModeNote({ mode, simulated = 'include' }) {
	if (!mode) return null
	const live = mode === 'live'
	const excluded = simulated === 'exclude'
	const sim = excluded
		? 'Decisions taken in simulation are excluded: the figures show real writes only.'
		: 'Decisions taken in simulation count like real ones and are marked SIM: nothing was written to the PMS for them.'
	return <AppNote severity={live ? 'error' : 'info'} sx={{ mb: 2, fontWeight: 600 }}>
		{live
			? `LIVE — the engine writes its prices to the PMS. ${sim}`
			: `SIMULATION — this account writes nothing to the PMS. ${sim}`}
	</AppNote>
}
